<template>
  <div class="panel">
    <div class="panel-title">{{ $t('hit-admin.notifications.tab-new') }}</div>

    <div class="panel-grid">
      <hit-select
        class="min-w-[28rem] w-full"
        :label="$t('hit-admin.notifications.recipient-type')"
        :options="recipientTypeOptions"
        :value="recipientType"
        item-id-property="id"
        :label-fields="['label']"
        clearable="false"
        @change="onRecipientTypeChange"
      />

      <hit-select
        v-if="needsCompany"
        class="min-w-[28rem] w-full"
        :label="$t('hit-admin.notifications.company')"
        :options="companyOptions"
        :value="companyId"
        item-id-property="id"
        :label-fields="['label']"
        clearable
        @change="
          (v) => {
            companyId = v;
            schedulePreview();
          }
        "
      />

      <div v-if="needsUser" class="user-search min-w-[28rem] w-full">
        <label class="block text-sm font-medium text-slate-700 mb-1">
          {{ $t('hit-admin.notifications.user') }}
        </label>
        <input
          v-model="userSearch"
          type="text"
          class="hit-input w-full"
          :placeholder="$t('hit-admin.notifications.user-search')"
          @input="onUserSearchInput"
        />
        <hit-select
          v-if="userOptions.length"
          class="mt-2 w-full"
          :options="userOptions"
          :value="userId"
          item-id-property="id"
          :label-fields="['label']"
          clearable
          @change="
            (v) => {
              userId = v;
              schedulePreview();
            }
          "
        />
      </div>
    </div>

    <div v-if="supportsCompanyFilters" class="mt-4 filter-block">
      <div class="text-sm font-medium text-slate-700 mb-2">
        {{ $t('hit-admin.notifications.company-status-filter') }}
      </div>
      <div class="status-checkboxes">
        <label
          v-for="s in verificationStatusOptions"
          :key="s.id"
          class="status-check"
        >
          <input
            v-model="selectedVerificationStatuses"
            type="checkbox"
            :value="s.id"
            @change="schedulePreview"
          />
          {{ s.label }}
        </label>
      </div>
      <label class="active-check mt-2">
        <input
          v-model="companyActiveOnly"
          type="checkbox"
          @change="schedulePreview"
        />
        {{ $t('hit-admin.notifications.company-active-only') }}
      </label>
    </div>

    <div v-if="previewLoading" class="preview-banner muted mt-3">…</div>
    <div v-else-if="previewWarning" class="preview-banner warn mt-3">
      {{ $t('hit-admin.notifications.filter-warning') }}: {{ previewWarning }}
    </div>
    <button
      v-else-if="previewCount !== null"
      type="button"
      class="preview-banner mt-3"
      :class="{clickable: previewCount > 0}"
      :disabled="previewCount === 0"
      @click="openRecipientsModal"
    >
      <template v-if="previewCount > 0">
        <span>
          {{
            $t('hit-admin.notifications.preview', {
              count: previewCount,
              users: previewUniqueUsers,
              emails: previewUniqueEmails,
            })
          }}
        </span>
        <span class="preview-hint">
          {{ $t('hit-admin.notifications.preview-click-hint') }}
        </span>
      </template>
      <template v-else>
        {{ $t('hit-admin.notifications.preview-empty') }}
      </template>
    </button>

    <notification-recipients-modal
      :open="recipientsModalOpen"
      :preview-params="previewParams"
      @close="recipientsModalOpen = false"
    />

    <div class="mt-4 grid gap-3">
      <div>
        <label class="block text-sm font-medium text-slate-700 mb-1">
          {{ $t('hit-admin.notifications.message-title') }}
        </label>
        <input v-model="title" type="text" class="hit-input w-full" />
      </div>
      <notification-message-locales
        ref="messageLocales"
        v-model="bodyByLocale"
        :preview-params="previewParams"
        :disabled="running"
      />
    </div>

    <div class="mt-3 flex flex-col gap-2">
      <label class="inline-flex items-center gap-2">
        <input v-model="sendEmail" type="checkbox" />
        {{ $t('hit-admin.notifications.send-email') }}
      </label>
      <div v-if="sendEmail">
        <label class="block text-sm font-medium text-slate-700 mb-1">
          {{ $t('hit-admin.notifications.email-subject') }}
        </label>
        <input v-model="emailSubject" type="text" class="hit-input w-full" />
        <label class="block text-sm font-medium text-slate-700 mb-1 mt-2">
          {{ $t('hit-admin.notifications.test-email') }}
        </label>
        <input v-model="testEmail" type="email" class="hit-input w-full" />
      </div>
    </div>

    <div class="mt-4 flex flex-wrap gap-2">
      <hit-button
        :label="$t('hit-admin.notifications.send')"
        color="accent"
        prefix-icon="send"
        :disabled="running || !canSend"
        @click="send"
      />
      <hit-button
        v-if="sendEmail && testEmail"
        :label="$t('hit-admin.notifications.send-test')"
        color="button"
        prefix-icon="email"
        :disabled="running || !title"
        @click="sendTest"
      />
      <hit-button
        :label="$t('hit-admin.notifications.reset')"
        color="button"
        prefix-icon="reset_changes"
        :disabled="running"
        @click="resetForm"
      />
    </div>

    <div v-if="result" class="result mt-4">
      <div class="result-title" :class="{ok: result.ok, ko: !result.ok}">
        {{ result.ok ? $t('hit-admin.notifications.sent') : 'Erreur' }}
      </div>
      <pre class="result-pre">{{ resultText }}</pre>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';
import NotificationRecipientsModal from './NotificationRecipientsModal.vue';
import NotificationMessageLocales from './NotificationMessageLocales.vue';

const CONFIRM_THRESHOLD = 500;

export default defineComponent({
  name: 'NotificationBroadcastForm',
  components: {
    HitButton,
    HitSelect,
    NotificationRecipientsModal,
    NotificationMessageLocales,
  },
  emits: ['sent'],
  data() {
    return {
      recipientTypeOptions: [],
      verificationStatusOptions: [],
      companyOptions: [],
      recipientType: 'all_users',
      companyId: null,
      userId: null,
      userSearch: '',
      userOptions: [],
      userSearchTimer: null,
      selectedVerificationStatuses: ['NEW', 'PENDING_APPROVAL', 'VALIDATED'],
      companyActiveOnly: true,
      title: '',
      bodyByLocale: {},
      sendEmail: false,
      emailSubject: '',
      testEmail: '',
      previewCount: null,
      previewUniqueUsers: 0,
      previewUniqueEmails: 0,
      previewWarning: null,
      previewLoading: false,
      previewTimer: null,
      recipientsModalOpen: false,
      running: false,
      result: null,
    };
  },
  computed: {
    selectedRecipientMeta() {
      return this.recipientTypeOptions.find((r) => r.id === this.recipientType);
    },
    needsCompany() {
      return this.selectedRecipientMeta?.requiresCompany ?? false;
    },
    needsUser() {
      return this.selectedRecipientMeta?.requiresUser ?? false;
    },
    supportsCompanyFilters() {
      return this.selectedRecipientMeta?.supportsCompanyFilters ?? false;
    },
    previewParams() {
      return this.buildPreviewParams();
    },
    canSend() {
      if (!this.title?.trim()) return false;
      if (!this.hasMessageContent()) return false;
      if (this.needsCompany && !this.companyId) return false;
      if (this.needsUser && !this.userId) return false;
      if (this.previewCount === 0) return false;
      return true;
    },
    resultText() {
      try {
        return JSON.stringify(this.result, null, 2);
      } catch {
        return String(this.result);
      }
    },
  },
  async mounted() {
    await Promise.all([
      this.loadRecipientTypes(),
      this.loadVerificationStatuses(),
      this.loadCompanyOptions(),
    ]);
    this.schedulePreview();
  },
  methods: {
    async loadRecipientTypes() {
      try {
        this.recipientTypeOptions =
          (await adminApi.listNotificationRecipientTypes()) || [];
      } catch {
        this.recipientTypeOptions = [];
      }
    },
    async loadVerificationStatuses() {
      try {
        this.verificationStatusOptions =
          (await adminApi.listNotificationVerificationStatuses()) || [];
      } catch {
        this.verificationStatusOptions = [];
      }
    },
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({limit: 500});
        const rows = res?.rows || [];
        this.companyOptions = rows.map((r) => {
          const label = [r.designation, r.vat_no].filter(Boolean).join(' – ');
          return {id: r.id, label: label || r.id};
        });
      } catch {
        this.companyOptions = [];
      }
    },
    onRecipientTypeChange(value) {
      this.recipientType = value;
      this.companyId = null;
      this.userId = null;
      this.userOptions = [];
      this.schedulePreview();
    },
    onUserSearchInput() {
      if (this.userSearchTimer) clearTimeout(this.userSearchTimer);
      this.userSearchTimer = setTimeout(() => this.searchUsers(), 350);
    },
    async searchUsers() {
      const q = (this.userSearch || '').trim();
      if (q.length < 2) {
        this.userOptions = [];
        return;
      }
      try {
        const res = await adminApi.getRegisteredUsers({
          page: 1,
          pageSize: 20,
          filterText: q,
        });
        this.userOptions = (res?.rows || []).map((u) => ({
          id: u.id,
          label: [u.first_name, u.last_name, u.username_email]
            .filter(Boolean)
            .join(' – '),
        }));
      } catch {
        this.userOptions = [];
      }
    },
    buildPreviewParams() {
      const statuses = this.supportsCompanyFilters
        ? this.selectedVerificationStatuses
        : null;
      return {
        recipientType: this.recipientType,
        companyId: this.companyId,
        userId: this.userId,
        companyVerificationStatuses:
          statuses && statuses.length ? statuses.join(',') : null,
        companyActiveOnly: this.companyActiveOnly,
      };
    },
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => this.fetchPreview(), 400);
    },
    openRecipientsModal() {
      if ((this.previewCount ?? 0) <= 0) return;
      this.recipientsModalOpen = true;
    },
    async fetchPreview() {
      if (this.needsCompany && !this.companyId) {
        this.previewCount = null;
        this.previewWarning = null;
        return;
      }
      if (this.needsUser && !this.userId) {
        this.previewCount = null;
        this.previewWarning = null;
        return;
      }
      this.previewLoading = true;
      try {
        const res = await adminApi.previewNotificationBroadcast(
          this.buildPreviewParams()
        );
        this.previewCount = res.recipient_count ?? 0;
        this.previewUniqueUsers = res.unique_user_count ?? 0;
        this.previewUniqueEmails = res.unique_email_count ?? 0;
        this.previewWarning = res.company_filter_warning || null;
      } catch {
        this.previewCount = null;
        this.previewWarning = null;
      } finally {
        this.previewLoading = false;
      }
    },
    hasMessageContent() {
      return Object.values(this.bodyByLocale || {}).some(
        (v) =>
          v &&
          String(v)
            .replace(/<[^>]*>/g, '')
            .trim().length > 0
      );
    },
    buildBodyByLocalePayload() {
      const out = {};
      for (const [loc, html] of Object.entries(this.bodyByLocale || {})) {
        const text = String(html || '')
          .replace(/<[^>]*>/g, '')
          .trim();
        if (text.length > 0) {
          out[loc] = html;
        }
      }
      return out;
    },
    primaryBodyForTest() {
      const bodies = this.buildBodyByLocalePayload();
      const keys = Object.keys(bodies);
      if (!keys.length) return '';
      return bodies['fr-BE'] || bodies[keys[0]] || '';
    },
    buildPayload() {
      const statuses = this.supportsCompanyFilters
        ? this.selectedVerificationStatuses
        : null;
      const bodyByLocale = this.buildBodyByLocalePayload();
      return {
        recipient_type: this.recipientType,
        company_id: this.companyId,
        user_id: this.userId,
        title: this.title.trim(),
        body_by_locale: bodyByLocale,
        body_is_html: true,
        send_email: this.sendEmail,
        email_subject: (this.emailSubject || '').trim() || null,
        company_verification_statuses:
          statuses && statuses.length ? statuses : null,
        company_active_only: this.companyActiveOnly,
        show: true,
        ttl: 0,
      };
    },
    async send() {
      if (!this.canSend) return;
      const count = this.previewCount ?? 0;
      if (count >= CONFIRM_THRESHOLD) {
        const msg = this.$t('hit-admin.notifications.confirm-send', {count});
        if (!window.confirm(msg)) return;
      }
      this.running = true;
      this.result = null;
      try {
        const res = await adminApi.createNotificationBroadcast(
          this.buildPayload()
        );
        this.result = res;
        this.$emit('sent', res);
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.running = false;
      }
    },
    async sendTest() {
      if (!this.testEmail || !this.title) return;
      this.running = true;
      try {
        const res = await adminApi.sendNotificationTest({
          recipient_email: this.testEmail.trim(),
          title: this.title.trim(),
          body: this.primaryBodyForTest(),
          body_is_html: true,
          send_email: true,
          email_subject: (this.emailSubject || '').trim() || null,
          locale: 'fr',
        });
        this.result = res;
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.running = false;
      }
    },
    resetForm() {
      this.recipientType = 'all_users';
      this.companyId = null;
      this.userId = null;
      this.userSearch = '';
      this.userOptions = [];
      this.selectedVerificationStatuses = [
        'NEW',
        'PENDING_APPROVAL',
        'VALIDATED',
      ];
      this.companyActiveOnly = true;
      this.title = '';
      this.bodyByLocale = {};
      this.sendEmail = false;
      this.emailSubject = '';
      this.result = null;
      this.schedulePreview();
    },
  },
});
</script>

<style lang="scss" scoped>
.panel {
  padding: 1rem 1.25rem;
  border-radius: 1rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.82);
}

.panel-title {
  font-weight: 700;
  font-size: 1.05rem;
  margin-bottom: 1rem;
}

.panel-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.filter-block {
  padding: 0.75rem 1rem;
  border-radius: 0.65rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid rgba(15, 23, 42, 0.06);
}

.status-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.status-check,
.active-check {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.875rem;
}

.preview-banner {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.65rem 0.85rem;
  border-radius: 0.5rem;
  background: rgba(0, 153, 124, 0.08);
  color: #03695a;
  font-size: 0.9rem;
  border: 1px solid transparent;
  &.clickable {
    cursor: pointer;
    &:hover {
      background: rgba(0, 153, 124, 0.14);
      border-color: rgba(0, 153, 124, 0.25);
    }
  }
  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
  .preview-hint {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.8rem;
    text-decoration: underline;
    opacity: 0.9;
  }
  &.warn {
    background: rgba(255, 107, 0, 0.1);
    color: #b45309;
  }
  &.muted {
    color: #64748b;
    background: #f1f5f9;
  }
}

.hit-input,
.hit-textarea {
  border: 1px solid rgba(15, 23, 42, 0.15);
  border-radius: 0.5rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.875rem;
}

.result-title {
  font-weight: 700;
}
.result-title.ok {
  color: #03695a;
}
.result-title.ko {
  color: #b91c1c;
}
.result-pre {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.6rem;
  max-height: 12rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
